export interface BookingMessageDispatchInput {
  referenceCode: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  teamName: string;
  customerName: string;
  customerPhone: string;
  ownerName?: string;
  ownerPhone?: string;
  ownerMessageNote?: string;
}

export interface BookingMessageDispatchResult {
  ok: boolean;
  provider: string;
  customer?: { sms?: boolean; whatsapp?: boolean };
  owner?: { sms?: boolean; whatsapp?: boolean };
  error?: string;
  queued?: boolean;
}

export interface BookingStatusDispatchInput {
  status: 'cancelled' | 'rescheduled';
  referenceCode: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  customerName: string;
  customerPhone: string;
  ownerName?: string;
  ownerPhone?: string;
  byRole: 'customer' | 'owner' | 'admin';
  note?: string;
  newDate?: string;
  newStartTime?: string;
  newEndTime?: string;
}

type QueueItem = {
  id: string;
  endpoint: '/api/messages/booking-confirmation' | '/api/messages/booking-status';
  payload: unknown;
  attempts: number;
  nextRetryAt: number;
  lastError?: string;
  createdAt: string;
};

const MESSAGE_QUEUE_KEY = 'halisaha_message_outbox_v1';
const MAX_RETRY = 6;

function safeReadQueue(): QueueItem[] {
  try {
    const raw = localStorage.getItem(MESSAGE_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWriteQueue(items: QueueItem[]) {
  localStorage.setItem(MESSAGE_QUEUE_KEY, JSON.stringify(items));
}

function enqueue(endpoint: QueueItem['endpoint'], payload: unknown, error?: string) {
  const list = safeReadQueue();
  list.unshift({
    id: `mq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    endpoint,
    payload,
    attempts: 0,
    nextRetryAt: Date.now() + 30_000,
    lastError: error,
    createdAt: new Date().toISOString(),
  });
  safeWriteQueue(list.slice(0, 200));
}

async function postJson(endpoint: QueueItem['endpoint'], payload: unknown): Promise<BookingMessageDispatchResult> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
  const response = await fetch(`${baseUrl}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const json = (await response.json()) as BookingMessageDispatchResult;
  if (!response.ok) {
    return {
      ok: false,
      provider: json.provider || 'unknown',
      error: json.error || 'Mesaj gönderimi başarısız oldu.',
    };
  }
  return json;
}

export async function flushMessageQueue(): Promise<{ delivered: number; kept: number }> {
  const now = Date.now();
  const list = safeReadQueue();
  if (list.length === 0) return { delivered: 0, kept: 0 };

  const next: QueueItem[] = [];
  let delivered = 0;

  for (const item of list) {
    if (item.nextRetryAt > now) {
      next.push(item);
      continue;
    }

    try {
      const result = await postJson(item.endpoint, item.payload);
      if (result.ok) {
        delivered += 1;
        continue;
      }
      const attempts = item.attempts + 1;
      if (attempts >= MAX_RETRY) continue;
      const waitMs = Math.min(60 * 60 * 1000, Math.pow(2, attempts) * 15000);
      next.push({
        ...item,
        attempts,
        nextRetryAt: Date.now() + waitMs,
        lastError: result.error || 'retry failed',
      });
    } catch (error: any) {
      const attempts = item.attempts + 1;
      if (attempts >= MAX_RETRY) continue;
      const waitMs = Math.min(60 * 60 * 1000, Math.pow(2, attempts) * 15000);
      next.push({
        ...item,
        attempts,
        nextRetryAt: Date.now() + waitMs,
        lastError: error?.message || 'network error',
      });
    }
  }

  safeWriteQueue(next);
  return { delivered, kept: next.length };
}

export async function dispatchBookingMessages(
  payload: BookingMessageDispatchInput
): Promise<BookingMessageDispatchResult> {
  try {
    return await postJson('/api/messages/booking-confirmation', payload);
  } catch (error: any) {
    enqueue('/api/messages/booking-confirmation', payload, error?.message);
    return {
      ok: false,
      provider: 'unknown',
      error: 'Mesaj servisi ulaşılamadı. Gönderim kuyruğa alındı.',
      queued: true,
    };
  }
}

export async function dispatchBookingStatusMessages(
  payload: BookingStatusDispatchInput
): Promise<BookingMessageDispatchResult> {
  try {
    return await postJson('/api/messages/booking-status', payload);
  } catch (error: any) {
    enqueue('/api/messages/booking-status', payload, error?.message);
    return {
      ok: false,
      provider: 'unknown',
      error: 'Durum mesajı gönderilemedi. Gönderim kuyruğa alındı.',
      queued: true,
    };
  }
}
